import * as THREE from 'three';
import { PuzzleElement } from './PuzzleElement';
import { MaskType } from '../masks/MaskManager';

export class MaskPickup extends PuzzleElement {
  public readonly maskType: MaskType;
  private pickupRadius: number = 2;
  private isCollected: boolean = false;
  private rotationSpeed: number = 1;
  private bobSpeed: number = 2;
  private bobHeight: number = 0.3;
  private baseY: number;
  private glowLight: THREE.PointLight;
  
  private static maskColors: Record<MaskType, number> = {
    [MaskType.HERMES]: 0xffa500,
    [MaskType.POSEIDON]: 0x00bcd4,
    [MaskType.ARES]: 0xf44336,
    [MaskType.HADES]: 0x9c27b0,
    [MaskType.APOLLO]: 0xffeb3b
  };
  
  constructor(position: THREE.Vector3, maskType: MaskType) {
    super(position);
    this.maskType = maskType;
    this.baseY = position.y;
    
    // Set color based on mask type
    const color = MaskPickup.maskColors[maskType];
    
    // Update mask material
    const maskMesh = this.mesh.children[0] as THREE.Mesh;
    maskMesh.material = new THREE.MeshStandardMaterial({
      color: color,
      roughness: 0.3,
      metalness: 0.7,
      emissive: color,
      emissiveIntensity: 0.3,
      side: THREE.DoubleSide
    });
    
    // Update ring material
    const ringMesh = this.mesh.children[1] as THREE.Mesh;
    ringMesh.material = new THREE.MeshStandardMaterial({
      color: color,
      roughness: 0.2,
      metalness: 0.9,
      emissive: color,
      emissiveIntensity: 0.2
    });
    
    // Add glow light
    this.glowLight = new THREE.PointLight(color, 2, 8);
    this.glowLight.position.set(0, 0, 0);
    this.mesh.add(this.glowLight);
  }
  
  protected createMesh(): THREE.Group {
    const group = new THREE.Group();
    
    // Create a simple mask shape using a scaled sphere with a flat front
    const geometry = new THREE.SphereGeometry(0.5, 16, 12, 0, Math.PI);
    const material = new THREE.MeshStandardMaterial({
      color: 0xffd700,
      roughness: 0.3,
      metalness: 0.7,
      side: THREE.DoubleSide
    });
    
    const mask = new THREE.Mesh(geometry, material);
    mask.scale.set(1, 1.3, 0.4);
    mask.rotation.y = Math.PI;
    mask.castShadow = true;
    
    // Add decorative ring around the mask
    const ringGeometry = new THREE.TorusGeometry(0.55, 0.05, 8, 24);
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: 0xffd700,
      roughness: 0.2,
      metalness: 0.9
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = Math.PI / 2;
    
    group.add(mask);
    group.add(ring);
    
    return group;
  }
  
  public update(deltaTime: number, playerPosition: THREE.Vector3): void {
    if (this.isCollected) return;
    
    // Rotate
    this.mesh.rotation.y += this.rotationSpeed * deltaTime;
    
    // Bob up and down
    const time = Date.now() * 0.001;
    this.mesh.position.y = this.baseY + Math.sin(time * this.bobSpeed) * this.bobHeight;
    
    // Pulse glow
    this.glowLight.intensity = 1 + Math.sin(time * 3) * 0.3;
  }
  
  public tryCollect(playerPosition: THREE.Vector3): boolean {
    if (this.isCollected) return false;
    
    const distance = playerPosition.distanceTo(this.position);
    
    if (distance < this.pickupRadius) {
      this.collect();
      return true;
    }
    
    return false;
  }
  
  private collect(): void {
    this.isCollected = true;
    this.mesh.visible = false;
    this.glowLight.intensity = 0;
  }
  
  public isCollectedState(): boolean {
    return this.isCollected;
  }
}
